"use client";

export type SystemMessage = { id: string; message: string; kind: "error" | "success"; at: number };

const MESSAGES_KEY = "hub:system-messages";
const SEEN_KEY = "hub:system-messages-seen";
const LIMIT = 50;
/** Same message inside this window replaces the previous entry instead of stacking. */
const DUPLICATE_WINDOW_MS = 2 * 60 * 1000;

/** Broadcast so the notification tray refreshes as soon as a message appears. */
export const SYSTEM_MESSAGE_EVENT = "tairoh-system-message";

function read(): SystemMessage[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(MESSAGES_KEY) ?? "[]");
    return Array.isArray(parsed) ? (parsed as SystemMessage[]).filter((entry) => entry && typeof entry.message === "string") : [];
  } catch {
    return [];
  }
}

function write(messages: SystemMessage[]) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages.slice(-LIMIT))); } catch { /* storage full or unavailable */ }
}

/** Stores a screen-top message (error / operation result) so the tray can list it later. */
export function recordSystemMessage(message: string, kind: "error" | "success" = "success") {
  if (typeof window === "undefined" || !message) return;
  const messages = read();
  // Repeating the same message within a short window (navigation, retries,
  // interval messages) must not fill the tray with duplicates.
  const duplicateIndex = messages.findLastIndex((entry) => entry.message === message && Date.now() - entry.at < DUPLICATE_WINDOW_MS);
  if (duplicateIndex >= 0) messages.splice(duplicateIndex, 1);
  messages.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, message, kind, at: Date.now() });
  write(messages);
  window.dispatchEvent(new CustomEvent(SYSTEM_MESSAGE_EVENT));
}

export function listSystemMessages(): SystemMessage[] {
  return read().reverse();
}

export function clearSystemMessages() {
  if (typeof window === "undefined") return;
  write([]);
  try { window.localStorage.setItem(SEEN_KEY, String(Date.now())); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(SYSTEM_MESSAGE_EVENT));
}

export function markSystemMessagesSeen() {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(SEEN_KEY, String(Date.now())); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(SYSTEM_MESSAGE_EVENT));
}

export function countUnseenSystemMessages(): number {
  if (typeof window === "undefined") return 0;
  const seen = Number(window.localStorage.getItem(SEEN_KEY) ?? 0);
  return read().filter((entry) => entry.at > seen).length;
}
